import React, { useCallback, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";

const JudgePicker = ({ value = [], onChange, disabled }) => {
  const token = useSelector((state) => state.auth.token);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const timerRef = useRef(null);

  const labelFor = (u) => {
    if (!u) return "";
    return (
      `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
      u.email ||
      `User #${u.id}`
    );
  };

  const fetchUsers = useCallback(
    (search) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/searchUsers?search=${encodeURIComponent(
              search || ""
            )}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            setOptions(
              (response.data.data || []).filter((u) => u && u.id != null)
            );
          } else {
            setOptions([]);
          }
        } catch (e) {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 350);
    },
    [token]
  );

  const safeValue = Array.isArray(value)
    ? value.filter((u) => u && u.id != null)
    : [];

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={options}
      value={safeValue}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => labelFor(option)}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      inputValue={inputValue}
      onOpen={() => fetchUsers(inputValue)}
      onInputChange={(_, newInput, reason) => {
        setInputValue(newInput);
        if (reason === "input" || reason === "clear") {
          fetchUsers(newInput);
        }
      }}
      onChange={(_, newValue) => {
        onChange((newValue || []).filter((u) => u && u.id != null));
        // Clear typed search after a judge is selected
        setInputValue("");
      }}
      clearOnBlur={false}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              avatar={
                <Avatar src={option.profile_img || option.profileImage}>
                  {(option.firstName || "U")[0]}
                </Avatar>
              }
              label={labelFor(option)}
              {...tagProps}
            />
          );
        })
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box
            component="li"
            key={key || option.id}
            {...rest}
            sx={{ gap: 1, display: "flex", alignItems: "center" }}
          >
            <Avatar
              src={option.profile_img || option.profileImage}
              sx={{ width: 28, height: 28 }}
            >
              {(option.firstName || "U")[0]}
            </Avatar>
            <Box>
              <Typography variant="body2">{labelFor(option)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search judges by name or email"
          sx={{
            "& .MuiOutlinedInput-root": {
              background: "#fff",
              borderRadius: "10px",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default JudgePicker;
